/**
 * The ticker's glyphs — fourteen inline SVGs on a 16-unit grid.
 *
 * **Inline SVG rather than characters, and rather than an icon package.** Unicode was the
 * cheap option and it is the wrong one: `✚ ⌁ ☠` render at three different weights, three
 * different baselines and three different widths depending on which fallback font the reader's
 * OS supplies, and a row of them looks like a ransom note. A package would be the other
 * mistake — kilobytes of tree-shaken tooling and a second visual language, for fourteen
 * shapes that are each four path commands.
 *
 * Every shape is stroked in `currentColor` at a shared weight, so the ticker's emphasis ramp
 * (`changeFamilies.ts`) colours the glyph by colouring the row, and nothing here knows about
 * weight at all.
 */
import type { ChangeGlyph } from '@/features/story/changeFamilies';

const PATHS: Record<ChangeGlyph, React.ReactNode> = {
  /** health — a cross, the one piece of medical iconography nobody misreads */
  cross: <path d="M8 3v10M3 8h10" />,
  /** stamina — a bolt */
  bolt: <path d="M9.5 2 4 9h3.5L6.5 14 12 7H8.5z" />,
  /** hunger — a bowl */
  bowl: (
    <>
      <path d="M2.5 7.5h11a5.5 5.5 0 0 1-11 0Z" />
      <path d="M6 4.5c0-1 1-1.2 1-2M9.5 4.5c0-1 1-1.2 1-2" />
    </>
  ),
  /** thirst — a drop */
  drop: <path d="M8 2.5c2.5 3 4 5 4 6.8a4 4 0 0 1-8 0C4 7.5 5.5 5.5 8 2.5Z" />,
  /** the four item kinds — a crate; `summary` says which way it went */
  box: (
    <>
      <path d="M2.5 5.5 8 2.8l5.5 2.7v5L8 13.2 2.5 10.5Z" />
      <path d="M2.5 5.5 8 8.2l5.5-2.7M8 8.2v5" />
    </>
  ),
  /** injuries — a dressing over a wound */
  bandage: (
    <>
      <rect x="2.5" y="6" width="11" height="4" rx="2" />
      <path d="M6.5 6v4M9.5 6v4" />
    </>
  ),
  /** status effects — a spark, for the things that are happening *to* you */
  spark: (
    <path d="M8 2v3.2M8 10.8V14M2 8h3.2M10.8 8H14M4 4l2.2 2.2M9.8 9.8 12 12M12 4 9.8 6.2M6.2 9.8 4 12" />
  ),
  /** skills — a chevron, the only thing in the game that only goes up */
  chevron: <path d="m3.5 9.5 4.5-4 4.5 4M3.5 13l4.5-4 4.5 4" />,
  /** recorded facts — a page */
  note: (
    <>
      <path d="M4 2.5h5.5L12 5v8.5H4Z" />
      <path d="M6 7.5h4M6 10h4" />
    </>
  ),
  /** locations and movement — a pin */
  pin: (
    <>
      <path d="M8 2.5a4 4 0 0 1 4 4c0 3-4 7-4 7s-4-4-4-7a4 4 0 0 1 4-4Z" />
      <circle cx="8" cy="6.5" r="1.4" />
    </>
  ),
  /** NPCs — a figure */
  person: (
    <>
      <circle cx="8" cy="5" r="2.3" />
      <path d="M3.5 13.5a4.5 4.5 0 0 1 9 0" />
    </>
  ),
  /** factions — a banner */
  flag: <path d="M4 14V2.5l8 2.5-8 2.5" />,
  /** the clock, which moves every turn */
  clock: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 4.5V8l2.5 1.8" />
    </>
  ),
  /** death */
  skull: (
    <>
      <path d="M8 2.2c3 0 5 2.2 5 5 0 1.9-1 3-1.7 3.6v1.9H4.7v-1.9C4 10.2 3 9.1 3 7.2c0-2.8 2-5 5-5Z" />
      <circle cx="6.2" cy="7.4" r="1.1" />
      <circle cx="9.8" cy="7.4" r="1.1" />
    </>
  ),
};

interface ChangeIconProps {
  glyph: ChangeGlyph;
}

/**
 * Decorative by declaration: the row it sits in already carries the family name and the
 * engine's `summary` in text, so announcing the shape as well would read the same change
 * twice (the accessibility commitment is fewer announcements, not more).
 */
export function ChangeIcon({ glyph }: ChangeIconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-[0.95em] shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {PATHS[glyph]}
    </svg>
  );
}
